/**
 * Player controller: on foot (camera-relative movement, jump, aim, collisions)
 * and in vehicle (controls mapping, seated humanoid, enter / exit / bail out).
 */
import * as THREE from 'three';
import { G } from './ctx';
import { Humanoid, type FullHumanoidStyle } from './humanoid';
import type { DamageSource, HeldWeaponKind, IPlayer, IVehicle } from './types';
import { clamp, damp, dampAngle } from './util';

const WALK_SPEED = 3.2;
const RUN_SPEED = 6.5;
const AIM_SPEED = 2.4;
const RADIUS = 0.4;
const JUMP_DUR = 0.5;
const JUMP_H = 0.5;
const ENTER_DUR = 0.45;
const EXIT_DUR = 0.3;

const _f = new THREE.Vector3();
const _r = new THREE.Vector3();
const _wish = new THREE.Vector3();
const _seat = new THREE.Vector3();
const _door = new THREE.Vector3();
const _tmp = new THREE.Vector3();

export class Player implements IPlayer {
  group = new THREE.Group();
  humanoid: Humanoid;
  pos = new THREE.Vector3();
  heading = 0;
  health = 100;
  armor = 0;
  dead = false;
  vehicle: IVehicle | null = null;
  transitioning = false;
  speed = 0;
  aiming = false;

  /** horizontal velocity on foot (m/s) */
  velocity = new THREE.Vector3();
  /** true while in the cosmetic jump arc */
  jumping = false;
  /** seconds left of the 'hit' stagger (input damped) */
  staggerT = 0;

  private jumpT = 0;
  private jumpY = 0;
  private transT = 0;
  private transFrom = new THREE.Vector3();
  private footPhase = 0;
  private lastVehicleHit = -10;
  private diedEmitted = false;
  private held: HeldWeaponKind = 'none';

  constructor(x: number, z: number, heading: number) {
    const style: FullHumanoidStyle = {
      skin: 0xdba97f,
      shirt: 0xeeeae0,
      pants: 0x2c3d6b,
      hair: 0x1b1411,
      shoes: 0xe8e8e4,
      hat: 'cap',
      scale: 1.0,
      jacket: 0x5a6b3c,
      hairstyle: 'short',
      longSleeves: true,
      eyes: 0x3b2a1a,
    };
    this.humanoid = new Humanoid(style);
    this.group.add(this.humanoid.group);
    this.pos.set(x, 0, z);
    this.heading = heading;
    this.group.position.copy(this.pos);
    this.group.rotation.y = heading;
  }

  // ----------------------------------------------------------------- api ----

  eyePosition(out: THREE.Vector3): THREE.Vector3 {
    if (this.vehicle) return this.vehicle.seatPosition(0, out).add(_tmp.set(0, 0.7, 0));
    return out.set(this.pos.x, this.pos.y + 1.6 * this.humanoid.scale + this.jumpY, this.pos.z);
  }

  aimDirection(out: THREE.Vector3): THREE.Vector3 {
    if (this.aiming && !this.vehicle) {
      const yaw = G.cam.yaw;
      const pitch = G.cam.pitch;
      const cp = Math.cos(pitch);
      return out.set(Math.sin(yaw) * cp, -Math.sin(pitch), Math.cos(yaw) * cp).normalize();
    }
    return out.set(Math.sin(this.heading), 0, Math.cos(this.heading));
  }

  update(dt: number): void {
    this.syncHeldWeapon();
    if (this.vehicle) this.updateInVehicle(dt);
    else this.updateOnFoot(dt);
  }

  applyDamage(amount: number, source: DamageSource): void {
    if (this.dead || amount <= 0) return;
    const absorbed = Math.min(this.armor, amount);
    this.armor -= absorbed;
    this.health -= amount - absorbed;
    G.hud.damageFlash();
    if (this.health <= 0) {
      this.health = 0;
      this.die(source);
    }
  }

  heal(amount: number): void {
    if (this.dead) return;
    this.health = clamp(this.health + amount, 0, 100);
  }

  addArmor(amount: number): void {
    this.armor = clamp(this.armor + amount, 0, 100);
  }

  tryEnterVehicle(): boolean {
    if (this.vehicle || this.dead || this.transitioning) return false;
    const v = G.entities.nearestVehicle(this.pos.x, this.pos.z, 3.5, (c) => !c.destroyed);
    if (!v) return false;
    const driver = v.driver;
    const stolen = driver !== null && driver !== 'player';
    if (stolen) {
      driver.exitVehicle();
      driver.scare(this.pos.x, this.pos.z);
    }
    v.driver = 'player';
    v.parked = false;
    v.playerTouched = true;
    v.persistent = true;
    v.lastPlayerContact = G.time.t;
    v.controls.throttle = 0;
    v.controls.steer = 0;
    v.controls.handbrake = false;
    v.controls.horn = false;
    this.vehicle = v;
    this.aiming = false;
    this.jumping = false;
    this.jumpT = 0;
    this.jumpY = 0;
    this.staggerT = 0;
    this.velocity.set(0, 0, 0);
    this.speed = 0;
    this.transitioning = true;
    this.transT = ENTER_DUR;
    this.transFrom.copy(this.pos);
    this.humanoid.setAnim('walk');
    this.humanoid.setHeldWeapon('none');
    G.events.emit('vehicleEntered', { vehicle: v, stolen });
    if (stolen) G.events.emit('crime', { kind: 'car_theft', x: v.pos.x, z: v.pos.z });
    return true;
  }

  exitVehicle(): void {
    this.leaveVehicle(false);
  }

  teleport(pos: THREE.Vector3, heading: number): void {
    if (this.vehicle) this.leaveVehicle(true);
    this.pos.set(pos.x, 0, pos.z);
    this.heading = heading;
    this.velocity.set(0, 0, 0);
    this.speed = 0;
    this.jumping = false;
    this.jumpT = 0;
    this.jumpY = 0;
    this.transitioning = false;
    this.transT = 0;
    this.staggerT = 0;
    this.group.position.copy(this.pos);
    this.group.rotation.y = heading;
  }

  respawn(pos: THREE.Vector3, heading: number): void {
    this.teleport(pos, heading);
    this.health = 100;
    this.armor = 0;
    this.dead = false;
    this.diedEmitted = false;
    this.aiming = false;
    this.humanoid.setAnim('idle');
    this.humanoid.update(0.001, 0);
  }

  // ------------------------------------------------------------ internals ----

  private die(source: DamageSource): void {
    this.dead = true;
    this.aiming = false;
    this.staggerT = 0;
    this.velocity.set(0, 0, 0);
    this.speed = 0;
    if (this.vehicle) {
      const c = this.vehicle.controls;
      c.throttle = 0;
      c.steer = 0;
      c.handbrake = true;
      c.horn = false;
    }
    this.humanoid.setAnim('dead');
    if (!this.diedEmitted) {
      this.diedEmitted = true;
      G.events.emit('playerDied', { source });
    }
  }

  private syncHeldWeapon(): void {
    const ws = G.weapons;
    const want: HeldWeaponKind = this.vehicle || this.dead || !ws ? 'none' : (ws.defs[ws.current]?.held ?? 'none');
    if (want !== this.held) {
      this.held = want;
      this.humanoid.setHeldWeapon(want);
    }
  }

  private stagger(): void {
    this.staggerT = 0.5;
    this.humanoid.setAnim('hit');
  }

  private leaveVehicle(silent: boolean): void {
    const v = this.vehicle;
    if (!v) return;
    const spd = Math.abs(v.speed);
    // humanoid back into the player group
    const hg = this.humanoid.group;
    this.group.add(hg);
    hg.position.set(0, 0, 0);
    hg.rotation.set(0, 0, 0);
    v.doorPosition(this.pos);
    this.pos.y = 0;
    this.heading = v.heading;
    v.controls.throttle = 0;
    v.controls.steer = 0;
    v.controls.handbrake = false;
    v.controls.horn = false;
    v.driver = null;
    v.lastPlayerContact = G.time.t;
    this.vehicle = null;
    this.transitioning = false;
    this.transT = 0;
    this.velocity.set(0, 0, 0);
    if (!silent) {
      if (spd > 8 && !this.dead) {
        // bail out: slide along the car's motion, take damage, stagger
        this.velocity.copy(v.velocity).multiplyScalar(0.55);
        this.velocity.y = 0;
        this.applyDamage(spd * 2, 'fall');
        if (!this.dead) this.stagger();
        G.cam.shake(Math.min(0.5, spd * 0.02), 0.35);
        G.particles.emit('dust', this.pos.x, 0.1, this.pos.z, 10);
      } else {
        this.transitioning = true;
        this.transT = EXIT_DUR;
        if (!this.dead) this.humanoid.setAnim('idle');
      }
      G.events.emit('vehicleExited', { vehicle: v });
    } else if (!this.dead) {
      this.humanoid.setAnim('idle');
    }
    G.city.clampToBounds(this.pos);
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;
  }

  private updateInVehicle(dt: number): void {
    const v = this.vehicle!;
    const inp = G.input;
    const locked = G.controlsLocked;
    v.lastPlayerContact = G.time.t;
    this.pos.copy(v.pos);
    this.pos.y = 0;
    this.heading = v.heading;
    this.speed = 0;
    this.aiming = false;
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;

    const hg = this.humanoid.group;
    v.seatPosition(0, _seat);
    _seat.y -= this.humanoid.hipHeight;

    if (this.transitioning) {
      this.transT -= dt;
      const k = 1 - clamp(this.transT / ENTER_DUR, 0, 1);
      v.doorPosition(_door);
      _door.y = 0;
      if (hg.parent !== this.group) {
        this.group.add(hg);
        hg.rotation.set(0, 0, 0);
      }
      if (k < 0.5) {
        // dash to the door
        const u = k / 0.5;
        _tmp.copy(this.transFrom).lerp(_door, u);
        hg.position.set(_tmp.x - this.pos.x, 0, _tmp.z - this.pos.z);
        const dx = _door.x - this.transFrom.x;
        const dz = _door.z - this.transFrom.z;
        const h = dx * dx + dz * dz > 0.01 ? Math.atan2(dx, dz) : v.heading;
        hg.rotation.y = h - this.heading;
        this.humanoid.setAnim('walk');
        this.humanoid.update(dt, 4);
      } else {
        // slide onto the seat
        const u = (k - 0.5) / 0.5;
        const ease = u * u * (3 - 2 * u);
        _tmp.copy(_door).lerp(_seat, ease);
        hg.position.set(_tmp.x - this.pos.x, _tmp.y, _tmp.z - this.pos.z);
        hg.rotation.y = 0;
        this.humanoid.setAnim('drive');
        this.humanoid.update(dt, 0);
      }
      v.controls.throttle = 0;
      v.controls.steer = 0;
      v.controls.handbrake = false;
      v.controls.horn = false;
      if (this.transT <= 0) {
        this.transitioning = false;
        this.transT = 0;
      }
      return;
    }

    // seated: parent the humanoid to the car so it moves with it inside the same frame
    if (hg.parent !== v.group) {
      v.group.add(hg);
      hg.rotation.set(0, 0, 0);
    }
    v.group.updateWorldMatrix(true, false);
    hg.position.copy(v.group.worldToLocal(_tmp.copy(_seat)));
    // undo any scale the vehicle group might carry
    hg.rotation.set(0, 0, 0);

    if (!locked && !this.dead) {
      v.controls.throttle = clamp(inp.moveY, -1, 1);
      v.controls.steer = clamp(inp.moveX, -1, 1);
      v.controls.handbrake = inp.handbrake;
      v.controls.horn = inp.horn;
    } else if (!this.dead) {
      v.controls.throttle = 0;
      v.controls.steer = 0;
      v.controls.handbrake = false;
      v.controls.horn = false;
    }
    if (!this.dead) this.humanoid.setAnim('drive');
    this.humanoid.update(dt, 0);
    if (inp.interact && !locked && !this.dead) this.exitVehicle();
  }

  private updateOnFoot(dt: number): void {
    const inp = G.input;
    const locked = G.controlsLocked;

    if (this.transitioning) {
      this.transT -= dt;
      if (this.transT <= 0) {
        this.transitioning = false;
        this.transT = 0;
      }
    }
    if (this.staggerT > 0) this.staggerT = Math.max(0, this.staggerT - dt);

    const canControl = !locked && !this.dead && !this.transitioning;
    let mx = canControl ? inp.moveX : 0;
    let my = canControl ? inp.moveY : 0;
    if (this.staggerT > 0) {
      mx *= 0.25;
      my *= 0.25;
    }
    this.aiming = canControl && inp.aim;

    // ---- movement (camera relative) --------------------------------------------
    G.cam.forward(_f);
    G.cam.right(_r);
    _wish.set(0, 0, 0).addScaledVector(_f, my).addScaledVector(_r, mx);
    let mag = _wish.length();
    if (mag > 1e-4) {
      const k = Math.min(1, mag);
      _wish.multiplyScalar(k / mag);
      mag = k;
    } else mag = 0;
    const max = this.aiming ? AIM_SPEED : inp.sprint ? RUN_SPEED : WALK_SPEED;
    const tx = _wish.x * max;
    const tz = _wish.z * max;
    const accelerating = tx * tx + tz * tz > this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z;
    const lam = accelerating ? 20 : 16;
    this.velocity.x = damp(this.velocity.x, tx, lam, dt);
    this.velocity.z = damp(this.velocity.z, tz, lam, dt);
    this.velocity.y = 0;
    if (this.dead) this.velocity.set(0, 0, 0);
    this.speed = Math.hypot(this.velocity.x, this.velocity.z);
    if (this.speed < 0.02) {
      this.velocity.set(0, 0, 0);
      this.speed = 0;
    }
    this.pos.x += this.velocity.x * dt;
    this.pos.z += this.velocity.z * dt;

    // ---- heading ----------------------------------------------------------------
    if (!this.dead) {
      if (this.aiming) this.heading = dampAngle(this.heading, G.cam.yaw, 22, dt);
      else if (this.speed > 0.3 && mag > 0) this.heading = dampAngle(this.heading, Math.atan2(this.velocity.x, this.velocity.z), 14, dt);
    }

    // ---- jump (cosmetic arc) -------------------------------------------------------
    if (canControl && inp.jump && !this.jumping && this.staggerT <= 0) {
      this.jumping = true;
      this.jumpT = 0;
    }
    if (this.jumping) {
      this.jumpT += dt;
      if (this.jumpT >= JUMP_DUR) {
        this.jumping = false;
        this.jumpT = 0;
        this.jumpY = 0;
        G.particles.emit('dust', this.pos.x, 0.05, this.pos.z, 8, 0, 1, 0);
        G.audio.play('footstep', this.pos.x, this.pos.z, 0.9);
      } else {
        this.jumpY = JUMP_H * Math.sin((Math.PI * this.jumpT) / JUMP_DUR);
      }
    }

    // ---- collisions ---------------------------------------------------------------
    if (!this.dead) {
      const hit = G.city.resolveCircle(this.pos.x, this.pos.z, RADIUS);
      if (hit) {
        this.pos.x = hit.x;
        this.pos.z = hit.z;
        // kill velocity into the wall
        const vn = this.velocity.x * hit.nx + this.velocity.z * hit.nz;
        if (vn < 0) {
          this.velocity.x -= hit.nx * vn;
          this.velocity.z -= hit.nz * vn;
        }
      }
      this.collideVehicles();
      this.pushPeds();
      G.city.clampToBounds(this.pos);
      this.pos.y = 0;
    }

    // ---- animation ----------------------------------------------------------------
    const hum = this.humanoid;
    if (this.dead) {
      hum.setAnim('dead');
    } else if (this.staggerT > 0) {
      // 'hit' was set by stagger(); keep it until it ends
    } else if (this.aiming) {
      hum.setAnim('aim');
      hum.setAimPitch(-G.cam.pitch);
    } else if (this.speed > 4.6) hum.setAnim('run');
    else if (this.speed > 0.25) hum.setAnim('walk');
    else if (hum.anim !== 'punch') hum.setAnim('idle');

    // footsteps (two per gait cycle), throttled by the cycle itself
    if (this.speed > 0.6 && !this.jumping && !this.dead) {
      const prev = Math.floor(this.footPhase);
      this.footPhase += (1.35 + this.speed * 0.26) * 2 * dt;
      if (Math.floor(this.footPhase) !== prev) G.audio.play('footstep', this.pos.x, this.pos.z, this.speed > 4.6 ? 0.8 : 0.45);
      if (this.footPhase > 1e6) this.footPhase = 0;
    }

    this.group.position.set(this.pos.x, this.jumpY, this.pos.z);
    this.group.rotation.y = this.heading;
    hum.update(dt, this.speed);

    if (canControl && inp.interact) this.tryEnterVehicle();
  }

  private collideVehicles(): void {
    const vehicles = G.entities.vehicles;
    const px = this.pos.x;
    const pz = this.pos.z;
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      const dx = v.pos.x - px;
      const dz = v.pos.z - pz;
      if (dx * dx + dz * dz > 36) continue;
      if (!v.containsPoint(this.pos.x, this.pos.z, 0.35)) continue;
      const s = Math.sin(v.heading);
      const c = Math.cos(v.heading);
      const rx = this.pos.x - v.pos.x;
      const rz = this.pos.z - v.pos.z;
      const fwd = rx * s + rz * c;
      const side = rx * c - rz * s;
      const hl = v.spec.length / 2 + 0.35;
      const hw = v.spec.width / 2 + 0.35;
      const ovF = hl - Math.abs(fwd);
      const ovS = hw - Math.abs(side);
      if (ovF <= 0 || ovS <= 0) continue;
      let nx: number;
      let nz: number;
      let depth: number;
      if (ovS < ovF) {
        const sg = side >= 0 ? 1 : -1;
        nx = c * sg;
        nz = -s * sg;
        depth = ovS;
      } else {
        const sg = fwd >= 0 ? 1 : -1;
        nx = s * sg;
        nz = c * sg;
        depth = ovF;
      }
      this.pos.x += nx * depth;
      this.pos.z += nz * depth;
      const vs = Math.hypot(v.velocity.x, v.velocity.z);
      if (vs > 3 && G.time.t - this.lastVehicleHit > 0.6) {
        this.lastVehicleHit = G.time.t;
        this.velocity.x += v.velocity.x * 0.9;
        this.velocity.z += v.velocity.z * 0.9;
        this.applyDamage(vs * 3, 'vehicle');
        if (!this.dead) this.stagger();
        G.cam.shake(Math.min(0.6, vs * 0.03), 0.4);
        G.audio.play('ped_hit', this.pos.x, this.pos.z, 0.8);
      } else {
        // slow push: remove velocity into the car
        const vn = this.velocity.x * nx + this.velocity.z * nz;
        if (vn < 0) {
          this.velocity.x -= nx * vn;
          this.velocity.z -= nz * vn;
        }
      }
    }
  }

  private pushPeds(): void {
    const peds = G.entities.peds;
    for (let i = 0; i < peds.length; i++) {
      const p = peds[i];
      if (p.state === 'dead' || p.vehicle) continue;
      const dx = p.pos.x - this.pos.x;
      const dz = p.pos.z - this.pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > 0.49 || d2 < 1e-6) continue;
      const d = Math.sqrt(d2);
      const push = (0.7 - d) * 0.6;
      this.pos.x -= (dx / d) * push;
      this.pos.z -= (dz / d) * push;
    }
  }
}
